// Activation Flow Layer — improves first-session workspace setup and trial conversion
//
// ActivationChecklistWidget — Dashboard widget that adapts based on the user's
// chosen activation_path.
//
// Completion types — three fundamentally different kinds of item completion:
//
//   AUTO-DETECTED completion:
//     Derived from live backend stats (e.g. total_clients > 0 → "Add first client").
//     These are accurate cross-device because they reflect backend state.
//     Stored in localStorage under the "auto" sub-key as a cache; re-evaluated
//     on every stats load so they self-correct if stats change.
//
//   VISIT-FLAG completion (page-visit based auto-complete):
//     Signaled by a page mounting and writing a flag via setActivationVisitFlag().
//     Stored under a separate localStorage key (activation_visit_{userId}_{flagKey}).
//     Items with visitFlagKey are navigation-only on click — they do NOT toggle
//     completion on click; they only complete once the real follow-up event fires.
//
//   MANUAL (UI-only) completion:
//     Toggled by the user clicking an item in this widget.
//     Currently stored in localStorage only — this is a UI-only convenience
//     flag and will NOT sync across devices or browsers.
//     TODO: Move manual completion flags to the backend user profile (or a
//     dedicated checklist collection) to enable cross-device persistence.
//     Stored in localStorage under the "manual" sub-key.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Progress } from "./ui/progress";
import { CheckCircle2, Circle, Rocket } from "lucide-react";

// ── Checklist definitions per activation_path ─────────────────────────────────
// Each item has:
//   key           — stable identifier used as storage key
//   label         — display text
//   link          — navigation target (optional, used when item is clicked)
//   autoCheck     — function(stats) -> bool  (auto-mark done from live stats)
//                   null means no auto-detection from stats
//   visitFlagKey  — string key for visit-flag completion (set by setActivationVisitFlag()).
//                   When present the item is navigation-only on click; it completes only
//                   after the real follow-up event (page visit) fires.
const PATH_CHECKLISTS = {
  invoice: [
    {
      key: "add_first_client",
      label: "Add your first client",
      link: "/clients",
      autoCheck: (stats) => (stats?.total_clients || 0) > 0,
      // auto-complete from backend stats; no manual toggle needed
    },
    {
      key: "create_first_invoice",
      label: "Create your first invoice",
      link: "/invoices",
      autoCheck: (stats) =>
        ((stats?.invoice_stats?.draft || 0) +
          (stats?.invoice_stats?.sent || 0) +
          (stats?.invoice_stats?.paid || 0)) > 0,
      // auto-complete from backend stats
    },
    {
      key: "view_dashboard_insights",
      label: "View dashboard insights",
      link: "/analytics",
      autoCheck: null, // manual only — no reliable stats signal
    },
  ],
  budget: [
    {
      key: "add_income_sources",
      label: "Add income sources",
      link: "/financials",
      autoCheck: null, // manual only
    },
    {
      key: "add_expense_categories",
      label: "Add expense categories",
      link: "/budget",
      autoCheck: null, // manual only
    },
    {
      key: "create_savings_goal",
      label: "Create savings goal",
      link: "/savings",
      autoCheck: null, // manual only
    },
  ],
  savings: [
    {
      key: "connect_savings_account",
      label: "Connect first savings account",
      link: "/savings",
      autoCheck: null, // manual only
    },
    {
      key: "create_target_goal",
      label: "Create target goal",
      link: "/savings",
      autoCheck: null, // manual only
    },
    {
      key: "track_savings_progress",
      label: "Track progress",
      link: "/analytics?tab=savings",
      autoCheck: null, // manual only
    },
  ],
  time: [
    {
      key: "start_first_timer",
      label: "Start first timer",
      link: "/time",
      autoCheck: (stats) => (stats?.today_minutes || 0) > 0,
      // auto-complete from backend stats
    },
    {
      key: "log_first_task",
      label: "Log first task entry",
      link: "/tasks",
      autoCheck: (stats) => (stats?.pending_tasks || 0) > 0,
      // auto-complete from backend stats
    },
    {
      key: "review_tracked_hours",
      label: "Review tracked hours",
      link: "/reports",
      autoCheck: null, // manual only — no reliable stats signal
    },
  ],
  explore: [
    {
      key: "watch_system_overview",
      label: "Watch system overview",
      link: "/training",
      autoCheck: null,
      // manual toggle — no programmatic signal that the user actually watched;
      // clicking navigates to Training and simultaneously marks done.
    },
    {
      key: "visit_dashboard_analytics",
      label: "Visit dashboard analytics",
      link: "/analytics",
      autoCheck: null,
      // visit-flag completion — auto-completes when AnalyticsPage mounts and
      // calls setActivationVisitFlag(userId, "analytics_visited").
      // Clicking this item only navigates; it does NOT toggle completion.
      visitFlagKey: "analytics_visited",
    },
    {
      key: "choose_next_tool",
      label: "Choose next workspace tool",
      link: "/dashboard",
      autoCheck: null,
      // visit-flag completion — auto-completes when the user navigates to any
      // qualifying workspace module (Clients, Invoices, Time Tracker, Financials,
      // Budget Planner, Savings, Tasks, Leads, Reports).
      // Layout.js watches location.pathname and calls
      // setActivationVisitFlag(userId, "workspace_tool_chosen") on a qualifying route.
      // Clicking this item only navigates to Dashboard; it does NOT toggle completion.
      visitFlagKey: "workspace_tool_chosen",
    },
  ],
};

// ── localStorage helpers ───────────────────────────────────────────────────────
// Completion state is stored as two separate sub-objects:
//   { auto: { [key]: true }, manual: { [key]: true } }
//
// Keeping them separate makes it straightforward to later migrate "manual"
// flags to backend storage without touching the "auto" cache.
const storageKey = (userId, path) => `activation_checklist_${userId}_${path}`;

function loadStore(userId, activationPath) {
  if (!userId || !activationPath) return { auto: {}, manual: {} };
  try {
    const stored = localStorage.getItem(storageKey(userId, activationPath));
    // JSON.parse(null) returns null; handle missing key explicitly
    const raw = stored !== null ? JSON.parse(stored) : null;
    return {
      auto: raw?.auto || {},
      manual: raw?.manual || {},
    };
  } catch {
    return { auto: {}, manual: {} };
  }
}

function saveStore(userId, activationPath, store) {
  if (!userId || !activationPath) return;
  localStorage.setItem(storageKey(userId, activationPath), JSON.stringify(store));
}

// ── Visit-flag helpers ─────────────────────────────────────────────────────────
// Visit flags are written by pages when they mount (e.g. AnalyticsPage writes
// "analytics_visited"; Layout watches qualifying routes and writes
// "workspace_tool_chosen").  The widget reads them directly in isDone().
const visitFlagStorageKey = (userId, flagKey) =>
  `activation_visit_${userId}_${flagKey}`;

/**
 * Persist a visit-based activation flag for the given user.
 * Call this from a page's useEffect on mount (or from a route watcher in Layout).
 * Safe to call even when userId is null — it becomes a no-op in that case.
 */
export function setActivationVisitFlag(userId, flagKey) {
  if (!userId || !flagKey) return;
  localStorage.setItem(visitFlagStorageKey(userId, flagKey), "1");
}

function getActivationVisitFlag(userId, flagKey) {
  if (!userId || !flagKey) return false;
  return localStorage.getItem(visitFlagStorageKey(userId, flagKey)) === "1";
}

// ── ActivationChecklistWidget ─────────────────────────────────────────────────
export default function ActivationChecklistWidget({ user, stats }) {
  const navigate = useNavigate();
  const activationPath = user?.activation_path;
  const userId = user?.id;

  const items = useMemo(
    () => (activationPath ? PATH_CHECKLISTS[activationPath] || [] : []),
    [activationPath]
  );

  // Completion store: { auto: {}, manual: {} }
  // auto  — updated by live stats (auto-detected, cross-device accurate)
  // manual — toggled by the user (UI-only, localStorage only for now)
  const loadFromStorage = useCallback(
    () => loadStore(userId, activationPath),
    [userId, activationPath]
  );

  const [store, setStore] = useState(loadFromStorage);

  // Re-load from storage when user/path changes
  useEffect(() => {
    setStore(loadFromStorage());
  }, [loadFromStorage]);

  // ── Auto-detected completion — re-evaluate on every stats update ───────────
  // Only touches the "auto" sub-object; never modifies manual flags.
  useEffect(() => {
    if (!stats || items.length === 0) return;

    const autoUpdates = {};
    items.forEach(({ key, autoCheck }) => {
      if (autoCheck && autoCheck(stats)) {
        autoUpdates[key] = true;
      }
    });
    if (Object.keys(autoUpdates).length === 0) return;

    setStore((prev) => {
      const next = { ...prev, auto: { ...prev.auto, ...autoUpdates } };
      saveStore(userId, activationPath, next);
      return next;
    });
  }, [stats, items, userId, activationPath]);

  // ── Manual completion — UI-only toggle ────────────────────────────────────
  // TODO: Replace localStorage persistence here with a backend API call
  //       (e.g. PATCH /auth/profile with a checklist_completed map) so that
  //       manual completions sync across devices and browsers.
  const toggleManual = (key) => {
    setStore((prev) => {
      const next = {
        ...prev,
        manual: { ...prev.manual, [key]: !prev.manual[key] },
      };
      saveStore(userId, activationPath, next);
      return next;
    });
  };

  // A key is considered done if:
  //   1. auto flag is set (stats-based or visit-flag-based auto-detection), OR
  //   2. manual flag is set (explicit user click on a manual-toggle item), OR
  //   3. a visitFlagKey is defined for the item and the corresponding localStorage flag is set.
  const isDone = (key, visitFlagKey) =>
    !!(store.auto[key] || store.manual[key] || getActivationVisitFlag(userId, visitFlagKey));

  if (!activationPath || items.length === 0) return null;

  const completedCount = items.filter(({ key, visitFlagKey }) => isDone(key, visitFlagKey)).length;
  const progressPct = Math.round((completedCount / items.length) * 100);
  const allDone = completedCount === items.length;

  // Hide widget once everything is done
  if (allDone) return null;

  const pathLabels = {
    invoice: "Invoice Workflow",
    budget: "Budget Setup",
    savings: "Savings Tracking",
    time: "Time Tracking",
    explore: "Workspace Exploration",
  };

  return (
    <Card data-testid="activation-checklist-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Rocket className="h-4 w-4 text-blue-500" />
          Getting Started — {pathLabels[activationPath] || activationPath}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{completedCount} of {items.length} completed</span>
            <span>{progressPct}%</span>
          </div>
          <Progress
            value={progressPct}
            className="h-1.5"
            aria-label={`Activation checklist progress: ${progressPct}%`}
          />
        </div>

        <div className="space-y-2" role="list">
          {items.map(({ key, label, link, autoCheck, visitFlagKey }) => {
            const done = isDone(key, visitFlagKey);
            // Auto-detected items (stats or visit-flag) cannot be manually un-toggled.
            // Navigation-only items (visitFlagKey set) navigate on click but do NOT
            // toggle manual completion — they wait for the real follow-up event.
            // Manual-only items (no autoCheck, no visitFlagKey) toggle on click.
            const isAutoDetected = !!autoCheck;
            const isNavigationOnly = !!visitFlagKey;
            return (
              <div
                key={key}
                role="listitem"
                aria-checked={done}
                className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                  done
                    ? "opacity-60 cursor-default"
                    : "cursor-pointer hover:bg-muted"
                }`}
                onClick={() => {
                  if (done) return;
                  // Navigation-only items: just navigate, never toggle manually.
                  // Auto-detected items: just navigate, completion comes from stats/visit.
                  // Manual-only items: toggle completion AND navigate.
                  if (!isAutoDetected && !isNavigationOnly) toggleManual(key);
                  if (link) navigate(link);
                }}
              >
                {done ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )}
                <span
                  className={`text-sm ${
                    done ? "line-through text-muted-foreground" : ""
                  }`}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
